/**
 * ExcludedAccountsSection -- read-only "available but not selected" account
 * list for the connector detail view (#285).
 *
 * For every OAUTH_POPULATED_DROPDOWN field on the connector, offers an
 * on-demand "Check for available accounts" button. On click it re-derives the
 * discovered set from the same discovery endpoint the wizard uses (stored-creds
 * mode) and shows the discovered options that are NOT in the persisted
 * selection, so a tenant can see what they could add without re-running OAuth.
 *
 * Discovery is a live, potentially expensive upstream API call, so it is never
 * fired automatically -- only when the user clicks the button.
 *
 * #2190: in the "selected plus any added later" scope the unchecked accounts
 * are DELIBERATELY excluded (the field's `excluded_field` deny-list), so they
 * are listed as excluded, not as available to add.
 *
 * Renders nothing for connectors without an oauth_populated_dropdown field.
 */
import { useCallback, useState } from 'react';
import { AlertCircle, Loader2, Search } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/lib/api/client';
import {
  buildStoredCredsBody,
  duplicateLabelValues,
  errorMessageForCode,
  extractErrorCode,
  normalizeSelection,
  parseDiscoveryOptions,
  type DiscoveryOption,
} from '@/lib/oauth/discovery';
import type { ConnectorFieldSchema, DropdownConfig } from '@/types/connector-schema';

interface Props {
  schemaFields: ConnectorFieldSchema[];
  /** Persisted connector settings (the saved selection lives here). */
  settings: Record<string, unknown>;
  connectorType: string;
  connectorName: string;
}

function isOAuthPopulatedDropdown(
  field: ConnectorFieldSchema,
): field is ConnectorFieldSchema & { dropdown_config: DropdownConfig } {
  return field.type === 'oauth_populated_dropdown' && Boolean(field.dropdown_config);
}

export function ExcludedAccountsSection({
  schemaFields,
  settings,
  connectorType,
  connectorName,
}: Props) {
  const oauthFields = schemaFields.filter(isOAuthPopulatedDropdown);
  if (oauthFields.length === 0) return null;

  return (
    <section className="border-t py-6" aria-label="Available accounts">
      <h3 className="text-sm font-semibold text-foreground">Available accounts</h3>
      <p className="mt-0.5 text-xs text-muted-foreground">
        Check which accounts your connection can see but that aren&apos;t part of this connector
        yet. Querying contacts the provider directly.
      </p>
      <div className="mt-4 space-y-6">
        {oauthFields.map((field) => (
          <ExcludedAccountsField
            key={field.name}
            field={field}
            settings={settings}
            connectorType={connectorType}
            connectorName={connectorName}
          />
        ))}
      </div>
    </section>
  );
}

interface FieldProps {
  field: ConnectorFieldSchema & { dropdown_config: DropdownConfig };
  settings: Record<string, unknown>;
  connectorType: string;
  connectorName: string;
}

function ExcludedAccountsField({ field, settings, connectorName }: FieldProps) {
  const config = field.dropdown_config;
  const noun = config.discovery_label ?? 'account(s)';

  const selected = normalizeSelection(settings[field.name]);
  const selectedSet = new Set(selected);
  // #2100: on an `empty_means_all` field a stored [] is the open-ended
  // sentinel -- every discovered option IS extracted, so nothing is excluded.
  // Reading it as "0 selected" listed every property as "not selected",
  // contradicting the field above.
  const openEnded =
    config.auto_select === 'all' && config.empty_means_all === true && selected.length === 0;
  // #2190: the deny-list of the "selected plus any added later" scope.
  const denied = new Set(
    openEnded && config.excluded_field ? normalizeSelection(settings[config.excluded_field]) : [],
  );

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [options, setOptions] = useState<DiscoveryOption[] | null>(null);

  const check = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const body = buildStoredCredsBody(config, connectorName, settings);
      const response = await apiClient.post(config.fetch_endpoint, body);
      setOptions(parseDiscoveryOptions(response.data, config));
    } catch (err) {
      setError(errorMessageForCode(extractErrorCode(err), err));
      setOptions(null);
    } finally {
      setIsLoading(false);
    }
  }, [config, connectorName, settings]);

  const excluded = openEnded ? [] : (options ?? []).filter((o) => !selectedSet.has(o.value));
  const deniedOptions = (options ?? []).filter((o) => denied.has(o.value));
  // #2207: same-named accounts show their id so the badges can be told apart.
  const duplicates = duplicateLabelValues(options ?? []);
  const badgeText = (o: DiscoveryOption) => (duplicates.has(o.value) ? `${o.label} (${o.value})` : o.label);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {field.label}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={check}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              Checking…
            </>
          ) : (
            <>
              <Search className="mr-2 h-3.5 w-3.5" />
              Check for available {noun}
            </>
          )}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!error && options !== null && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            {denied.size > 0
              ? `${options.length - deniedOptions.length} included`
              : openEnded
                ? `All ${noun}`
                : `${selected.length} selected`}{' '}
            · {options.length} discovered
          </p>
          {options.length === 0 ? (
            <p className="text-sm text-muted-foreground">No {noun} discovered.</p>
          ) : denied.size > 0 ? (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                All other discovered {noun} are extracted, including any added later.
              </p>
              {deniedOptions.length > 0 && (
                <>
                  <div className="text-xs text-muted-foreground">
                    Deliberately excluded ({deniedOptions.length})
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {deniedOptions.map((o) => (
                      <Badge key={o.value} variant="outline" title={o.value}>
                        {badgeText(o)}
                      </Badge>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : openEnded ? (
            <p className="text-sm text-muted-foreground">
              All discovered {noun} are extracted, including any added later.
            </p>
          ) : excluded.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              All discovered {noun} are already selected.
            </p>
          ) : (
            <div className="space-y-2">
              <div className="text-xs text-muted-foreground">
                Available but not selected ({excluded.length})
              </div>
              <div className="flex flex-wrap gap-2">
                {excluded.map((o) => (
                  <Badge key={o.value} variant="secondary" title={o.value}>
                    {badgeText(o)}
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Edit the configuration above and save to add any of these.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
